import jsPDF from "jspdf";
import {
  nhStats,
  nhFactSheet,
  nhAlignmentRows,
  nhEmployerSignals,
  nhTradeTracks,
  nhSalaryLadder,
  nhDistrictInitiatives,
  nhCdbgAlignment,
  nhDeploymentScale,
} from "./northHoustonData";

/**
 * Generates the North Houston District Bridge Proposal 02 proposal PDF.
 * Mirrors the Baytown pdfGenerator structure but loads NH data + tone.
 */
export function generateNHProposalPDF() {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const navy = [10, 25, 47];
  const red = [139, 0, 0];
  const slate = [51, 65, 85];
  const muted = [100, 116, 139];
  const gold = [184, 134, 11];
  const cream = [249, 247, 241];
  const margin = 56;
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const contentW = pageW - margin * 2;
  let y = margin;
  let pageNum = 0;

  const addPageIfNeeded = (need = 24) => {
    if (y + need > pageH - margin - 28) {
      addFooter();
      doc.addPage();
      pageNum += 1;
      y = margin + 8;
      addHeaderRule();
    }
  };

  const addHeaderRule = () => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...gold);
    doc.text("THE BRIDGE MODEL  ·  PROPOSAL 02  ·  NORTH HOUSTON DISTRICT", margin, margin - 22);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...muted);
    doc.text("thebridgemodel.org  ·  CAGE 9GH66", pageW - margin, margin - 22, { align: "right" });
    doc.setDrawColor(...gold);
    doc.setLineWidth(0.5);
    doc.line(margin, margin - 14, pageW - margin, margin - 14);
  };

  const addFooter = () => {
    const fy = pageH - 28;
    doc.setDrawColor(...gold);
    doc.setLineWidth(0.4);
    doc.line(margin, fy - 10, pageW - margin, fy - 10);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...gold);
    doc.text("THE BRIDGE MODEL  ·  PROPOSAL 02  ·  NORTH HOUSTON DISTRICT  ·  PROPOSED — PENDING DISTRICT BOARD APPROVAL", margin, fy);
    doc.setTextColor(...muted);
    doc.text(`thebridgemodel.org  ·  Page ${pageNum + 1}`, pageW - margin, fy, { align: "right" });
  };

  const heading = (text, opts = {}) => {
    const size = opts.size || 18;
    addPageIfNeeded(size + 12);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(size);
    doc.setTextColor(...(opts.color || navy));
    doc.text(text, margin, y);
    y += size + 6;
  };

  const eyebrow = (text) => {
    addPageIfNeeded(18);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(...red);
    doc.text(text.toUpperCase(), margin, y);
    y += 14;
  };

  const para = (text, opts = {}) => {
    const sz = opts.size || 10.5;
    const lh = opts.lh || 14.5;
    doc.setFont(opts.font || "times", opts.style || "normal");
    doc.setFontSize(sz);
    doc.setTextColor(...(opts.color || slate));
    const lines = doc.splitTextToSize(text, opts.width || contentW);
    lines.forEach((l) => {
      addPageIfNeeded(lh + 2);
      doc.text(l, opts.x || margin, y);
      y += lh;
    });
    y += opts.gap ?? 8;
  };

  const bullet = (text) => {
    const lh = 14;
    doc.setFont("times", "normal");
    doc.setFontSize(10.5);
    doc.setTextColor(...slate);
    const lines = doc.splitTextToSize(text, contentW - 18);
    lines.forEach((l, i) => {
      addPageIfNeeded(lh + 2);
      if (i === 0) {
        doc.setFillColor(...red);
        doc.rect(margin + 1, y - 7, 4, 4, "F");
      }
      doc.text(l, margin + 16, y);
      y += lh;
    });
    y += 3;
  };

  const statRow = (rows) => {
    addPageIfNeeded(60);
    const colW = contentW / rows.length;
    rows.forEach((r, i) => {
      const x = margin + i * colW;
      doc.setFillColor(...cream);
      doc.rect(x, y, colW - 6, 54, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(15);
      doc.setTextColor(...navy);
      doc.text(r.v, x + 10, y + 22);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.setTextColor(...muted);
      const lines = doc.splitTextToSize(r.k.toUpperCase(), colW - 18);
      lines.slice(0, 2).forEach((line, li) => {
        doc.text(line, x + 10, y + 36 + li * 9);
      });
    });
    y += 64;
  };

  const ruleSpacer = () => {
    addPageIfNeeded(20);
    doc.setDrawColor(...gold);
    doc.setLineWidth(0.4);
    doc.line(margin, y, pageW - margin, y);
    y += 14;
  };

  // ============ COVER PAGE ============
  addHeaderRule();
  y = margin + 30;

  doc.setFillColor(...navy);
  doc.rect(0, y - 10, pageW, pageH - y - 30, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...gold);
  doc.text("THE BRIDGE MODEL  ·  PROPOSAL 02", margin, y + 24);

  // PROPOSED status pill — pending approval
  doc.setDrawColor(...gold);
  doc.setLineWidth(0.5);
  doc.roundedRect(margin, y + 36, 360, 18, 2, 2);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  doc.setTextColor(...gold);
  doc.text("●  PROPOSED  ·  PENDING DISTRICT BOARD APPROVAL  ·  NOT YET ADOPTED", margin + 10, y + 49);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(34);
  doc.setTextColor(...cream);
  doc.text("North Houston", margin, y + 108);
  doc.text("District", margin, y + 146);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(13);
  doc.setTextColor(...gold);
  doc.text("From Greenspoint to Distribution Corridor", margin, y + 176);
  doc.text("The workforce behind the transformation.", margin, y + 196);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...cream);
  const summary =
    "A municipal service-connector proposal routing district households into the corridor's full employer base — IAH aviation, Amazon, FedEx, Sysco, Coca-Cola, Panelmatic, allied health — without displacing the residents who weathered the harder years.";
  doc.splitTextToSize(summary, contentW - 40).forEach((l, i) => {
    doc.text(l, margin, y + 238 + i * 14);
  });

  // Bottom cover meta
  const coverFooterY = pageH - 80;
  doc.setDrawColor(...gold);
  doc.setLineWidth(0.6);
  doc.line(margin, coverFooterY, pageW - margin, coverFooterY);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor(...gold);
  doc.text("PROPRIETOR & PROGRAM DESIGNER", margin, coverFooterY + 14);
  doc.text("FEDERAL REGISTRATION", pageW - margin, coverFooterY + 14, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...cream);
  doc.text("George Broughton III  ·  Sterile Mate LLC", margin, coverFooterY + 30);
  doc.text("SAM.gov Active  ·  CAGE 9GH66  ·  All Awards", pageW - margin, coverFooterY + 30, { align: "right" });

  // ============ PAGE 2 — EXEC SUMMARY ============
  addFooter();
  doc.addPage();
  pageNum += 1;
  y = margin + 8;
  addHeaderRule();

  eyebrow("Executive Summary · The Ask in 60 Seconds");
  heading("Proposal 02 of a federally-replicable model.");
  para(
    "The North Houston District has executed one of the most consequential community revitalization stories in Texas — from the Greenspoint of the early 2000s into a 2:1 industrial-to-office corridor with 20M+ sq ft of warehouse and distribution space anchored 4 miles from IAH. The infrastructure is rebuilt. The employers are arriving. What the moment demands now is the trained human chain that connects the ~67,450 residents who live in the district to the jobs the district is producing. That chain is The Bridge Model."
  );
  para(
    "Bridge is a Tri-Portfolio Hub-and-Spoke model. The North Houston District acts as Hub coordinator. Three competitively-selected local nonprofit agencies serve as Spokes — each responsible for a 400-household portfolio. Federal CDBG funds, paired with Harris County and Texas State matches, finance the program. Annual independent evaluation. Caseload cap of 1:38. Same-day intake. Year-one investment: $3.75M serving 1,200 households — roughly $260 per household per month for intensive, multigenerational case management. Scale calibrated to the District's ~67,450-resident population (approximately 80% of the Baytown baseline)."
  );

  statRow([
    { v: "1,200", k: "Households served (Year 1)" },
    { v: "$3.75M", k: "Year-1 investment (all-in)" },
    { v: "$260 / mo", k: "Per-household case management" },
    { v: "5 Yrs", k: "Independent evaluation horizon" },
  ]);

  ruleSpacer();

  eyebrow("District Snapshot · ACS & North Houston Corporate");
  statRow([
    { v: `${nhStats.population.toLocaleString()}`, k: "District residents" },
    { v: `$${(nhStats.median_household_income / 1000).toFixed(1)}k`, k: "Median household income" },
    { v: `${nhStats.poverty_rate_pct}%`, k: "Overall poverty rate" },
    { v: `${nhStats.hispanic_latino_pct}%`, k: "Hispanic / Latino" },
  ]);
  statRow([
    { v: `${nhStats.black_alone_pct}%`, k: "Black / African American" },
    { v: `${nhStats.industrial_sqft_million}M+`, k: "Sq ft industrial in district" },
    { v: `${nhStats.industrial_to_office_ratio}`, k: "Industrial-to-office ratio" },
    { v: `${nhStats.iah_distance_miles} mi`, k: "From district center to IAH" },
  ]);

  // ============ PAGE 3 — STRATEGIC ALIGNMENT ============
  doc.addPage();
  pageNum += 1;
  y = margin + 8;
  addHeaderRule();

  eyebrow("Strategic Alignment · North Houston District Service Plan");
  heading("We don't compete with the District's plan. We make it possible.");
  para(
    "Bridge Proposal 02 is mapped directly to the District's published priorities — Public Safety, Workforce + Community Revitalization, Planning & Infrastructure, and Anti-Displacement. The District has rebuilt the corridor's infrastructure and recruited the employers. Bridge supplies the trained human chain that connects long-time residents to the jobs the District is producing.",
    { gap: 10 }
  );

  nhAlignmentRows.forEach((r) => {
    addPageIfNeeded(50);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...navy);
    doc.text(r.priority, margin, y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...red);
    doc.text(`[${r.strength.toUpperCase()}]`, pageW - margin, y, { align: "right" });
    y += 14;
    para(r.contribution, { size: 10, lh: 13.5, gap: 10 });
  });

  ruleSpacer();
  eyebrow("Active in the District · 2025");
  nhDistrictInitiatives.forEach((s) => bullet(s));

  // ============ PAGE — TRADE / CORRIDOR PIPELINE ============
  doc.addPage();
  pageNum += 1;
  y = margin + 8;
  addHeaderRule();

  eyebrow("Corridor Pipeline · Employer Signals");
  heading("The corridor is hiring. Bridge connects the workforce.");
  para(
    "The North Houston District is the largest single concentration of warehouse + distribution employment in greater Houston. Bridge routes district households into the full corridor pipeline — Amazon, FedEx, Sysco, Coca-Cola, Panelmatic, IAH ground services, Memorial Hermann Northeast — turning at-risk young adults into credentialed operators and homeowners within five years.",
    { gap: 12 }
  );

  nhEmployerSignals.forEach((e) => {
    addPageIfNeeded(70);
    doc.setFillColor(...cream);
    doc.rect(margin, y, contentW, 64, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...red);
    doc.text(e.employer.toUpperCase(), margin + 12, y + 16);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.setTextColor(...navy);
    doc.text(e.headline, margin + 12, y + 42);
    doc.setFont("times", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(...slate);
    const lines = doc.splitTextToSize(e.body, contentW - 130);
    lines.slice(0, 3).forEach((l, i) => doc.text(l, margin + 130, y + 24 + i * 12));
    doc.setFont("helvetica", "italic");
    doc.setFontSize(7.5);
    doc.setTextColor(...muted);
    doc.text(e.source, margin + 130, y + 58);
    y += 74;
  });

  ruleSpacer();
  eyebrow("Six Corridor Tracks · All Locally Hired");
  nhTradeTracks.forEach((t, i) => {
    addPageIfNeeded(50);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...navy);
    doc.text(`${i + 1}. ${t.name}`, margin, y);
    doc.setFont("times", "italic");
    doc.setFontSize(9);
    doc.setTextColor(...red);
    doc.text(`Start: ${t.startingPay}  ·  Mid: ${t.midPay}  ·  ${t.timeToCredential}`, margin, y + 13);
    y += 26;
    para(`${t.program} — ${t.why}`, { size: 9.5, lh: 13, gap: 8 });
  });

  // ============ PAGE — 5-YEAR LADDER ============
  doc.addPage();
  pageNum += 1;
  y = margin + 8;
  addHeaderRule();

  eyebrow("The 5-Year Ladder · Intake to Homeowner");
  heading("From under $22,000 to homeowner in the corridor.");
  para(
    "The salary progression Bridge tracks for every district resident on the family-track pipeline. Each rung is a verified North Houston corridor wage benchmark — not a promise, but a documented path with employers hiring at every step.",
    { gap: 10 }
  );

  nhSalaryLadder.forEach((r, i) => {
    addPageIfNeeded(56);
    const highlight = r.highlight;
    doc.setFillColor(...(highlight ? navy : cream));
    doc.rect(margin, y, contentW, 50, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(...(highlight ? gold : red));
    doc.text(r.year.toUpperCase(), margin + 12, y + 16);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.setTextColor(...(highlight ? cream : navy));
    doc.text(r.income, margin + 12, y + 36);
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9);
    doc.setTextColor(...(highlight ? gold : muted));
    doc.text(r.label, margin + 130, y + 16);
    doc.setFont("times", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(...(highlight ? cream : slate));
    const lines = doc.splitTextToSize(r.detail, contentW - 145);
    lines.slice(0, 2).forEach((l, li) => doc.text(l, margin + 130, y + 32 + li * 12));
    y += 60;
  });

  // ============ PAGE — CDBG ALIGNMENT ============
  doc.addPage();
  pageNum += 1;
  y = margin + 8;
  addHeaderRule();

  eyebrow("CDBG Alignment · Houston + Harris County");
  heading("Five CDBG objectives. Bridge advances all five — in the district.");
  para(
    "The City of Houston and Harris County both operate CDBG entitlement programs whose adopted objectives map directly to the Bridge Model. Proposal 02 is not a duplicate program — it is the connecting layer that makes existing CDBG objectives actually reach the residents of the North Houston District.",
    { gap: 12 }
  );

  nhCdbgAlignment.forEach((row) => {
    addPageIfNeeded(60);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...(row.primary ? red : navy));
    doc.text(`${row.id}. ${row.cityObjective}${row.primary ? "  ★ PRIMARY FIT" : ""}`, margin, y);
    y += 16;
    para(`Need — ${row.cityNeed}`, { size: 9.5, lh: 13, gap: 4, style: "italic", color: muted });
    para(`Bridge — ${row.bridgeContribution}`, { size: 10, lh: 13.5, gap: 10 });
  });

  // ============ PAGE — DISTRICT FACT SHEET ============
  doc.addPage();
  pageNum += 1;
  y = margin + 8;
  addHeaderRule();

  eyebrow("District Fact Sheet");
  heading("Verified numbers behind Proposal 02.");
  para(
    "All figures are derived from U.S. Census ACS 5-Year (Harris County tract series, district boundaries) and North Houston District corporate publications (2025). Multi-year trend lines published online at thebridgemodel.org/north-houston are illustrative from ACS / FBI UCR data.",
    { gap: 10 }
  );

  nhFactSheet.forEach((f) => {
    addPageIfNeeded(20);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(...navy);
    doc.text(f.v, margin, y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(...slate);
    doc.text(f.k, margin + 90, y);
    y += 18;
  });

  ruleSpacer();
  eyebrow("Federal Readiness");
  para(
    "Sterile Mate LLC is an actively SAM.gov-registered federal entity (CAGE Code 9GH66; All Awards — grants and contracts). A parallel federal coordination track is in motion with HUD Secretary Scott Turner's office, framing North Houston District alongside Baytown as the first two proposed deployments of a methodology the federal government can endorse and replicate.",
    { gap: 12 }
  );

  ruleSpacer();
  eyebrow("Contact");
  para("George Broughton III", { font: "helvetica", style: "bold", size: 12, color: navy, gap: 2 });
  para("Proprietor & Program Designer · The Bridge Model", { size: 10, gap: 2 });
  para("Founder & CEO · Sterile Mate LLC", { size: 10, gap: 2 });
  para("george.broughton@sterilemate.com  ·  thebridgemodel.org/north-houston", { size: 10, color: gold });

  addFooter();
  doc.save("Bridge-Model-Proposal-02-North-Houston-District.pdf");
}
